import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { minus, plus } from '@amzn/storm-ui-icons';

import { CustomIconButton } from '../../../../../UiComponentsLibrary/CustomIconButton';
import { CustomIconButtonColor } from '../../../../../UiComponentsLibrary/CustomIconButton/constants/CustomIconButtonColor';
import { IMP_CONTEXTUAL_NODE_FIELDS } from '../../constants/DetailedBookingConstants';
import {
  IMPContextualNodeData,
  IMPContextualNodeErrors,
} from '../../interface/DetailedBookingInterface';
import {
  ContextualNodeContainer,
  ContextualNodeHeader,
  ContextualNodeRow,
  ContextualNodeTitle,
  RemoveButtonContainer,
} from '../CreateBookingForm/CreateDetailedBookingForm.styles';
import ContextualNodeFieldIMP, {
  PartialContextualFieldProps,
} from './ContextualNodeFieldIMP';

interface ContextualNodesSectionProps {
  contextualNodes: IMPContextualNodeData[];
  setContextualNodes: Dispatch<SetStateAction<IMPContextualNodeData[]>>;
  setContextualNodeErrors: Dispatch<SetStateAction<IMPContextualNodeErrors[]>>;
  partialContextualNodeFieldProps: PartialContextualFieldProps;
}

const contextualNodeRow = (
  node: IMPContextualNodeData,
  removeContextualNode: (nodeId: string) => void,
  contextualNodes: IMPContextualNodeData[],
  partialContextualNodeFieldProps: PartialContextualFieldProps,
  t: (key: string) => string
) => {
  return (
    <ContextualNodeContainer key={node.id}>
      <ContextualNodeRow>
        <RemoveButtonContainer>
          <CustomIconButton
            icon={minus}
            label={t('remove-contextual-node-label')}
            backgroundColor={CustomIconButtonColor.delete}
            handleOnClickCb={() => removeContextualNode(node.id)}
            disabled={contextualNodes.length <= 1}
          />
        </RemoveButtonContainer>
        {IMP_CONTEXTUAL_NODE_FIELDS.map((fieldConfig) => {
          const nodeProps = {
            node,
            fieldConfig,
            ...partialContextualNodeFieldProps,
          };
          return (
            <ContextualNodeFieldIMP key={fieldConfig.key} {...nodeProps} />
          );
        })}
      </ContextualNodeRow>
    </ContextualNodeContainer>
  );
};

const ContextualNodesSectionIMP = ({
  contextualNodes,
  setContextualNodes,
  setContextualNodeErrors,
  partialContextualNodeFieldProps,
}: ContextualNodesSectionProps) => {
  const { t } = useTranslation();

  const addContextualNode = () => {
    const newNode: IMPContextualNodeData = {
      id: Date.now().toString(),
      imSegment: '',
      valueToBeLockedAtPackageLevel: 0,
      impressions: 0,
      approxShareBlockedOverNode: 0,
    };
    setContextualNodes((prev) => [...prev, newNode]);
  };

  const removeContextualNode = (nodeId: string) => {
    if (contextualNodes.length > 1) {
      const nodeIndex = contextualNodes.findIndex((node) => node.id === nodeId);
      setContextualNodes((prev) => prev.filter((node) => node.id !== nodeId));
      setContextualNodeErrors((prev) =>
        prev.filter((_, index) => index !== nodeIndex)
      );
    }
  };

  const maxNodesAllowed =
    partialContextualNodeFieldProps.slotPackagesData.find(
      (slotPackage) =>
        slotPackage.slotPackageName ===
        partialContextualNodeFieldProps.selectedSlotPackage
    )?.segments.length || 0;

  const isAddButtonDisabled =
    !partialContextualNodeFieldProps.selectedSlotPackage ||
    contextualNodes.length === maxNodesAllowed; // When the nodes max out the number of possible nodes, + button is disabled

  return (
    <>
      <ContextualNodeHeader id="imp-contextual-node-header">
        <ContextualNodeTitle>{t('contextual-node-title')}</ContextualNodeTitle>
        <CustomIconButton
          icon={plus}
          label={t('add-contextual-node-label')}
          backgroundColor={CustomIconButtonColor.search}
          handleOnClickCb={addContextualNode}
          disabled={isAddButtonDisabled}
        />
      </ContextualNodeHeader>
      {contextualNodes.map((node) =>
        contextualNodeRow(
          node,
          removeContextualNode,
          contextualNodes,
          partialContextualNodeFieldProps,
          t
        )
      )}
    </>
  );
};

export default ContextualNodesSectionIMP;
